import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Calendar, Clock, Plus, CheckCircle, XCircle, RefreshCw, Send, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ABSENCE_TYPES = {
  FERIE: { label: 'Ferie', color: 'bg-blue-100 text-blue-800' },
  PERMESSO: { label: 'Permesso', color: 'bg-purple-100 text-purple-800' },
  PERMESSO_ROL: { label: 'ROL', color: 'bg-indigo-100 text-indigo-800' },
  MALATTIA: { label: 'Malattia', color: 'bg-yellow-100 text-yellow-800' },
  INFORTUNIO: { label: 'Infortunio', color: 'bg-red-100 text-red-800' },
  CONGEDO: { label: 'Congedo', color: 'bg-teal-100 text-teal-800' },
  ALTRO: { label: 'Altro', color: 'bg-gray-100 text-gray-800' }
};

const ATTENDANCE_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  SUBMITTED: { label: 'Inviato', color: 'bg-blue-100 text-blue-800' },
  APPROVED: { label: 'Approvato', color: 'bg-green-100 text-green-800' },
  LOCKED: { label: 'Bloccato', color: 'bg-red-100 text-red-800' }
};

const ABSENCE_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  SUBMITTED: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-800' },
  APPROVED: { label: 'Approvata', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Rifiutata', color: 'bg-red-100 text-red-800' },
  CANCELLED: { label: 'Annullata', color: 'bg-gray-100 text-gray-800' }
};

const MONTHS_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

export default function StaffDetail() {
  const { staffMemberId } = useParams();
  const navigate = useNavigate();
  const { token, selectedCondominium, userRole } = useAuth();
  
  const [staffMember, setStaffMember] = useState(null);
  const [attendance, setAttendance] = useState({ entries: [], total_hours: 0, total_overtime: 0, status: 'DRAFT' });
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  
  const [attendanceForm, setAttendanceForm] = useState({
    check_in_time: '08:00',
    check_out_time: '17:00',
    hours_worked: 8,
    overtime_hours: 0,
    notes: ''
  });
  
  const [absenceForm, setAbsenceForm] = useState({
    type: 'FERIE',
    start_date: '',
    end_date: '',
    hours: null,
    description: ''
  });

  const condoId = selectedCondominium?.condominium?.id;
  const isAdmin = userRole === 'ADMIN';
  const isConsultant = userRole === 'LABOR_CONSULTANT';
  const canApprove = isAdmin;

  const fetchStaffMember = useCallback(async () => {
    if (!token || !condoId) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        setStaffMember(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [token, condoId, staffMemberId]);

  const fetchAttendance = useCallback(async () => {
    if (!token || !condoId) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}/attendance?year=${selectedYear}&month=${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        setAttendance(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [token, condoId, staffMemberId, selectedYear, selectedMonth]);

  const fetchAbsences = useCallback(async () => {
    if (!token || !condoId) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}/absences?year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setAbsences(data.absences || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [token, condoId, staffMemberId, selectedYear]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStaffMember(), fetchAttendance(), fetchAbsences()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStaffMember, fetchAttendance, fetchAbsences]);

  const saveAttendance = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}/attendance`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            date: selectedDate,
            ...attendanceForm
          })
        }
      );
      
      if (res.ok) {
        toast.success('Presenza salvata');
        setAttendanceDialogOpen(false);
        fetchAttendance();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const submitMonth = async () => {
    try {
      const formData = new FormData();
      formData.append('year', selectedYear.toString());
      formData.append('month', selectedMonth.toString());
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}/attendance/submit-month`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Presenze inviate per approvazione');
        fetchAttendance();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore invio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const approveMonth = async (lock = false) => {
    try {
      const formData = new FormData();
      formData.append('year', selectedYear.toString());
      formData.append('month', selectedMonth.toString());
      formData.append('lock', lock.toString());
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}/attendance/approve-month`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success(lock ? 'Presenze bloccate' : 'Presenze approvate');
        fetchAttendance();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore approvazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const saveAbsence = async () => {
    if (!absenceForm.start_date || !absenceForm.end_date) {
      toast.error('Seleziona le date');
      return;
    }
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}/absences`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(absenceForm)
        }
      );
      
      if (res.ok) {
        toast.success('Richiesta assenza inviata');
        setAbsenceDialogOpen(false);
        setAbsenceForm({ type: 'FERIE', start_date: '', end_date: '', hours: null, description: '' });
        fetchAbsences();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const approveAbsence = async (absenceId, approved) => {
    try {
      const formData = new FormData();
      formData.append('approved', approved.toString());
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMemberId}/absences/${absenceId}/approve`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success(approved ? 'Richiesta approvata' : 'Richiesta rifiutata');
        fetchAbsences();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const openAttendanceDialog = (dateStr) => {
    setSelectedDate(dateStr);
    
    // Check if entry exists
    const existing = attendance.entries?.find(e => e.date === dateStr);
    if (existing) {
      setAttendanceForm({
        check_in_time: existing.check_in_time || '08:00',
        check_out_time: existing.check_out_time || '17:00',
        hours_worked: existing.hours_worked || 8,
        overtime_hours: existing.overtime_hours || 0,
        notes: existing.notes || ''
      });
    } else {
      setAttendanceForm({
        check_in_time: '08:00',
        check_out_time: '17:00',
        hours_worked: 8,
        overtime_hours: 0,
        notes: ''
      });
    }
    
    setAttendanceDialogOpen(true);
  };

  const getDaysInMonth = () => {
    const days = [];
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      const entry = attendance.entries?.find(e => e.date === dateStr);
      const dayAbsences = absences.filter(a => 
        a.status === 'APPROVED' && a.start_date <= dateStr && a.end_date >= dateStr
      );
      
      days.push({
        day,
        dateStr,
        dayOfWeek: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'][dayOfWeek],
        isWeekend,
        entry,
        absences: dayAbsences
      });
    }
    
    return days;
  };

  if (loading || !staffMember) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const days = getDaysInMonth();
  const monthStatus = ATTENDANCE_STATUS[attendance.status] || ATTENDANCE_STATUS.DRAFT;

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <PageHeader
        title="Dettaglio Dipendente"
        subtitle={`${staffMember.first_name} ${staffMember.last_name} — ${staffMember.role_type} • ${staffMember.employment_type}`}
        actions={
          <>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => navigate('/staff')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Badge className={monthStatus.color}>
              {monthStatus.label}
            </Badge>
          </>
        }
      />

      {/* Month Selector */}
      <div className="flex items-center gap-4">
        <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS_IT.map((month, i) => (
              <SelectItem key={i + 1} value={(i + 1).toString()}>{month}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2023, 2024, 2025, 2026].map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex-1" />
        
        {attendance.status === 'DRAFT' && (
          <Button variant="outline" onClick={submitMonth}>
            <Send className="h-4 w-4 mr-2" />
            Invia per Approvazione
          </Button>
        )}
        
        {canApprove && attendance.status === 'SUBMITTED' && (
          <>
            <Button variant="outline" onClick={() => approveMonth(false)}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Approva
            </Button>
            <Button onClick={() => approveMonth(true)}>
              <Lock className="h-4 w-4 mr-2" />
              Approva e Blocca
            </Button>
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Giorni Lavorati</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{attendance.total_days || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ore Totali</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(attendance.total_hours || 0).toFixed(1)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Straordinari</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {(attendance.total_overtime || 0).toFixed(1)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Assenze Approvate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {absences.filter(a => a.status === 'APPROVED').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="attendance">
        <TabsList>
          <TabsTrigger value="attendance">
            <Clock className="h-4 w-4 mr-2" />
            Presenze
          </TabsTrigger>
          <TabsTrigger value="absences">
            <Calendar className="h-4 w-4 mr-2" />
            Assenze
          </TabsTrigger>
        </TabsList>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Registro Presenze - {MONTHS_IT[selectedMonth - 1]} {selectedYear}</CardTitle>
              <CardDescription>
                Clicca su un giorno per inserire/modificare le ore
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
                  <div key={d} className="text-center text-xs font-medium py-2 text-muted-foreground">
                    {d}
                  </div>
                ))}
                
                {/* Empty cells for alignment */}
                {Array.from({ length: (new Date(selectedYear, selectedMonth - 1, 1).getDay() + 6) % 7 }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                
                {/* Days */}
                {days.map(({ day, dateStr, isWeekend, entry, absences: dayAbsences }) => {
                  const hasEntry = !!entry;
                  const hasAbsence = dayAbsences.length > 0;
                  const absenceType = hasAbsence ? ABSENCE_TYPES[dayAbsences[0].type] : null;
                  
                  return (
                    <div
                      key={day}
                      onClick={() => !hasAbsence && openAttendanceDialog(dateStr)}
                      className={`
                        p-2 rounded-md text-center cursor-pointer transition-colors min-h-[60px]
                        ${isWeekend ? 'bg-muted/50' : 'bg-background hover:bg-muted'}
                        ${hasEntry && !hasAbsence ? 'bg-green-50 border border-green-200' : ''}
                        ${hasAbsence ? absenceType?.color + ' cursor-not-allowed' : ''}
                      `}
                    >
                      <div className="font-medium text-sm">{day}</div>
                      {hasEntry && !hasAbsence && (
                        <div className="text-xs text-green-600 mt-1">
                          {entry.hours_worked}h
                          {entry.overtime_hours > 0 && <span className="text-orange-600">+{entry.overtime_hours}</span>}
                        </div>
                      )}
                      {hasAbsence && (
                        <div className="text-xs mt-1">{absenceType?.label}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Absences Tab */}
        <TabsContent value="absences" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Richieste Assenza - {selectedYear}</CardTitle>
                <CardDescription>
                  Ferie, permessi, malattie e infortuni
                </CardDescription>
              </div>
              <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nuova Richiesta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuova Richiesta Assenza</DialogTitle>
                    <DialogDescription>
                      Inserisci i dettagli della richiesta
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select 
                        value={absenceForm.type} 
                        onValueChange={(v) => setAbsenceForm({...absenceForm, type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ABSENCE_TYPES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data Inizio</Label>
                        <Input
                          type="date"
                          value={absenceForm.start_date}
                          onChange={(e) => setAbsenceForm({
                            ...absenceForm, 
                            start_date: e.target.value,
                            end_date: absenceForm.end_date || e.target.value
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Fine</Label>
                        <Input
                          type="date"
                          value={absenceForm.end_date}
                          onChange={(e) => setAbsenceForm({...absenceForm, end_date: e.target.value})}
                        />
                      </div>
                    </div>
                    {absenceForm.type === 'PERMESSO' && (
                      <div className="space-y-2">
                        <Label>Ore (per permessi brevi)</Label>
                        <Input
                          type="number"
                          value={absenceForm.hours || ''}
                          onChange={(e) => setAbsenceForm({...absenceForm, hours: parseFloat(e.target.value) || null})}
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Note</Label>
                      <Input
                        value={absenceForm.description}
                        onChange={(e) => setAbsenceForm({...absenceForm, description: e.target.value})}
                        placeholder="Motivo o note..."
                      />
                    </div>
                    {(absenceForm.type === 'MALATTIA' || absenceForm.type === 'INFORTUNIO') && (
                      <div className="p-3 rounded-lg bg-yellow-50 text-yellow-800 text-sm">
                        <strong>Nota:</strong> Per malattia e infortunio sarà possibile caricare il certificato dopo {"l'invio"}.
                      </div>
                    )}
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAbsenceDialogOpen(false)}>
                      Annulla
                    </Button>
                    <Button onClick={saveAbsence}>
                      Invia Richiesta
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {absences.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna richiesta di assenza per {selectedYear}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Periodo</TableHead>
                      <TableHead>Giorni/Ore</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Note</TableHead>
                      {canApprove && <TableHead className="text-right">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {absences.map((absence) => {
                      const typeInfo = ABSENCE_TYPES[absence.type] || ABSENCE_TYPES.ALTRO;
                      const statusInfo = ABSENCE_STATUS[absence.status] || ABSENCE_STATUS.DRAFT;
                      
                      // Calculate days
                      let daysCount = 1;
                      try {
                        const start = new Date(absence.start_date);
                        const end = new Date(absence.end_date);
                        daysCount = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                      } catch (e) {
                        // Date parsing error, keep default
                      }
                      
                      return (
                        <TableRow key={absence.id}>
                          <TableCell>
                            <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(absence.start_date).toLocaleDateString('it-IT')}
                            {absence.start_date !== absence.end_date && (
                              <> - {new Date(absence.end_date).toLocaleDateString('it-IT')}</>
                            )}
                          </TableCell>
                          <TableCell>
                            {absence.hours ? `${absence.hours}h` : `${daysCount} gg`}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">
                            {absence.description || '-'}
                          </TableCell>
                          {canApprove && (
                            <TableCell className="text-right">
                              {absence.status === 'SUBMITTED' && (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => approveAbsence(absence.id, true)}
                                    className="text-green-600"
                                    title="Approva"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => approveAbsence(absence.id, false)}
                                    className="text-red-600"
                                    title="Rifiuta"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Attendance Entry Dialog */}
      <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Presenza - {selectedDate}</DialogTitle>
            <DialogDescription>
              Inserisci le ore lavorate
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ora Ingresso</Label>
                <Input
                  type="time"
                  value={attendanceForm.check_in_time}
                  onChange={(e) => setAttendanceForm({...attendanceForm, check_in_time: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Ora Uscita</Label>
                <Input
                  type="time"
                  value={attendanceForm.check_out_time}
                  onChange={(e) => setAttendanceForm({...attendanceForm, check_out_time: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ore Lavorate</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={attendanceForm.hours_worked}
                  onChange={(e) => setAttendanceForm({...attendanceForm, hours_worked: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Straordinari</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={attendanceForm.overtime_hours}
                  onChange={(e) => setAttendanceForm({...attendanceForm, overtime_hours: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input
                value={attendanceForm.notes}
                onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                placeholder="Note opzionali..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttendanceDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={saveAttendance}>
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
